/**
 * Cliente HTTP del panel.
 *
 * Responsabilidades, todas deliberadamente estrechas:
 *  - adjuntar el token de sesión;
 *  - normalizar el error de la API (`{ error: { code, message, details } }`)
 *    a un `ApiError` que la interfaz pueda mostrar tal cual;
 *  - avisar del 401 una sola vez, para que la sesión caducada desemboque en
 *    la pantalla de acceso y no en un fallo silencioso por pantalla.
 *
 * No conoce React ni TanStack Query: así es comprobable con un `fetch` falso.
 */

export interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Sesión ausente o caducada: la interfaz debe devolver al acceso. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Sin permiso (rol insuficiente) o tenant bloqueado. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export type TokenReader = () => string | null;

export interface ApiClientOptions {
  baseUrl?: string;
  getToken: TokenReader;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** Cabecera `Idempotency-Key`: evita duplicar un DeCA si se reintenta. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Respuesta binaria (PDF) en vez de JSON. */
  responseType?: "json" | "blob";
  /**
   * Un 401 de esta petición NO se interpreta como sesión caducada. Para acciones
   * que piden un código o la contraseña y responden 401 si están mal (MFA, PIN):
   * el error se muestra en pantalla en vez de cerrar la sesión.
   */
  keepSessionOn401?: boolean;
}

/** Serializa la query omitiendo los filtros vacíos (no manda `?status=`). */
export function buildQueryString(
  query: Record<string, string | number | boolean | undefined | null> | undefined,
): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly _getToken: TokenReader;
  private readonly onUnauthorized: (() => void) | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this._getToken = options.getToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    const token = this._getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    if (options.body !== undefined) headers["content-type"] = "application/json";
    if (options.idempotencyKey) headers["idempotency-key"] = options.idempotencyKey;

    const response = await this.fetchImpl(
      `${this.baseUrl}${path}${buildQueryString(options.query)}`,
      {
        method: options.method ?? "GET",
        headers,
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      },
    );

    if (response.status === 204) return undefined as T;

    if (!response.ok) {
      let payload: ApiErrorPayload = {};
      try {
        payload = (await response.json()) as ApiErrorPayload;
      } catch {
        // Respuesta sin cuerpo JSON (proxy, 502 de Nginx…): se sintetiza un
        // error legible en vez de dejar el fallo sin explicación.
      }
      const error = new ApiError(
        response.status,
        payload.error?.code ?? "unknown_error",
        payload.error?.message ?? `La API respondió ${response.status}`,
        payload.error?.details,
      );
      // Se avisa solo del 401: un 403 es un "no puedes", no una sesión caída.
      if (error.status === 401 && !options.keepSessionOn401) this.onUnauthorized?.();
      throw error;
    }

    if (options.responseType === "blob") return (await response.blob()) as T;
    if (response.status === 205) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  get<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PATCH", body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }

  /**
   * Envía un `FormData` (multipart/form-data) como POST.
   * No establece `content-type` manualmente: el navegador lo pone
   * automáticamente con el boundary correcto al pasar un FormData a fetch.
   */
  async postForm<T>(path: string, body: FormData): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    const token = this._getToken();
    if (token) headers.authorization = `Bearer ${token}`;

    const response = await this.fetchImpl(
      `${this.baseUrl}${path}`,
      { method: "POST", headers, body },
    );

    if (response.status === 204) return undefined as T;

    if (!response.ok) {
      let payload: ApiErrorPayload = {};
      try {
        payload = (await response.json()) as ApiErrorPayload;
      } catch { /* sin cuerpo JSON */ }
      const error = new ApiError(
        response.status,
        payload.error?.code ?? "unknown_error",
        payload.error?.message ?? `La API respondió ${response.status}`,
        payload.error?.details,
      );
      if (error.status === 401) this.onUnauthorized?.();
      throw error;
    }

    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  /** Devuelve el token de sesión actual. Útil para construir URLs de descarga. */
  getToken(): string | null {
    return this._getToken();
  }
}
